import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import Iframe from "react-iframe";
import { Redirect, useParams, Link } from "react-router-dom";
import {
  FaBus,
  FaCheck,
  FaRoute,
  FaSyncAlt,
  FaFileAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import MdlSelectSession from "../dialog/MdlSelectSession";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import uploadIcon from "../resources/images/upload-icon.png";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const ALL_ROUTE_ID = "ALL";

const FALLBACK_PRINTABLE = {
  sample_id: "",
  sample_name: "Transport Student List",
  file_directory: `${process.env.REACT_APP_TARGET_DIR || ""}Printables/Report/`,
  file_name: "transport_report.php",
};

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function resolveSession() {
  const session = SessionManager.getCurrentSession();
  if (Array.isArray(session) && session[0]) {
    return {
      id: String(session[0].session_id || ""),
      name: String(session[0].session_name || ""),
      status: String(session[0].status || ""),
    };
  }
  if (session && typeof session === "object") {
    return {
      id: String(session.session_id || ""),
      name: String(session.session_name || ""),
      status: String(session.status || ""),
    };
  }
  return { id: "", name: "", status: "" };
}

const TransportReport = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const { document_type } = useParams();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [samples, setSamples] = useState([]);
  const [selectedSampleId, setSelectedSampleId] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState(ALL_ROUTE_ID);
  const [fileUrl, setFileUrl] = useState("");
  const [height, setHeight] = useState("420px");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const iframeRef = useRef(null);
  const bootstrappedRef = useRef(false);

  const selectedSample = useMemo(() => {
    if (!samples.length) return FALLBACK_PRINTABLE;
    return (
      samples.find((s) => String(s.sample_id) === String(selectedSampleId)) ||
      samples[0] ||
      FALLBACK_PRINTABLE
    );
  }, [samples, selectedSampleId]);

  const selectedRoute = useMemo(() => {
    if (selectedRouteId === ALL_ROUTE_ID) {
      return { route_id: ALL_ROUTE_ID, route_name: "All Routes" };
    }
    return (
      routes.find((r) => String(r.route_id) === String(selectedRouteId)) || {
        route_id: ALL_ROUTE_ID,
        route_name: "All Routes",
      }
    );
  }, [routes, selectedRouteId]);

  const buildFileUrl = useCallback((sample, route, sid) => {
    if (!sid) return "";
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return "";

    //Created By Cursor — always resolve to transport_report.php for this page
    let directory = String(
      sample?.file_directory || FALLBACK_PRINTABLE.file_directory || ""
    ).trim();
    let fileName = String(
      sample?.file_name || FALLBACK_PRINTABLE.file_name || ""
    ).trim();

    if (!/transport_report\.php$/i.test(fileName)) {
      fileName = FALLBACK_PRINTABLE.file_name;
      if (!directory) {
        directory = FALLBACK_PRINTABLE.file_directory;
      }
    }
    if (!directory) {
      directory = FALLBACK_PRINTABLE.file_directory;
    }
    if (directory && !directory.endsWith("/")) {
      directory += "/";
    }

    const params = new URLSearchParams();
    params.set("route_id", route?.route_id || ALL_ROUTE_ID);
    params.set("route_name", route?.route_name || "All Routes");
    params.set("session_id", sid);
    params.set("school_code", schoolCode);
    if (sample?.sample_id) params.set("sample_id", sample.sample_id);
    if (process.env.REACT_APP_SYSTEM_CODE) {
      params.set("system_code", process.env.REACT_APP_SYSTEM_CODE);
    }

    return `${process.env.REACT_APP_SERVER_NAME}${directory}${fileName}?${params.toString()}`;
  }, []);

  const refreshPreview = useCallback(
    (sample, route, sid) => {
      const url = buildFileUrl(sample, route, sid);
      if (!url) {
        setFileUrl("");
        setIframeLoading(false);
        return;
      }
      console.log("[TransportReport] URL>>>", url);
      setIframeLoading(true);
      setHeight("420px");
      setFileUrl(url);
    },
    [buildFileUrl]
  );

  const loadPageData = useCallback(
    async ({ soft = false, sid } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      const activeSessionId = sid || sessionId || resolveSession().id;

      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!activeSessionId) {
        myToast.notify_warning("Session is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const [routesRes, samplesRes] = await Promise.all([
          axios.get(`${API_BASE()}getRouteList.php`, {
            params: { school_code: schoolCode },
          }),
          axios.get(`${API_BASE()}getPrintableSample.php`, {
            params: {
              school_code: schoolCode,
              document_type: document_type || "",
            },
          }),
        ]);

        const routePayload = routesRes?.data;
        const nextRoutes =
          routePayload?.api_status === "OK" && Array.isArray(routePayload.data)
            ? routePayload.data
            : [];
        setRoutes(nextRoutes);

        const samplePayload = samplesRes?.data;
        const nextSamples =
          samplePayload?.api_status === "OK" &&
          Array.isArray(samplePayload.data) &&
          samplePayload.data.length
            ? samplePayload.data
            : [FALLBACK_PRINTABLE];
        setSamples(nextSamples);

        const sample =
          nextSamples.find(
            (s) => String(s.sample_id) === String(selectedSampleId)
          ) || nextSamples[0];
        if (sample?.sample_id) {
          setSelectedSampleId(String(sample.sample_id));
        } else if (!selectedSampleId) {
          setSelectedSampleId("");
        }

        let route = {
          route_id: ALL_ROUTE_ID,
          route_name: "All Routes",
        };
        if (selectedRouteId !== ALL_ROUTE_ID) {
          const found = nextRoutes.find(
            (r) => String(r.route_id) === String(selectedRouteId)
          );
          if (found) {
            route = {
              route_id: found.route_id,
              route_name: found.route_name || "Selected route",
            };
          } else {
            setSelectedRouteId(ALL_ROUTE_ID);
          }
        }

        refreshPreview(sample, route, activeSessionId);
      } catch (error) {
        console.error("[TransportReport] load error", error);
        setRoutes([]);
        setSamples([FALLBACK_PRINTABLE]);
        myToast.notify_warning("Failed to load transport report data.");
        refreshPreview(
          FALLBACK_PRINTABLE,
          { route_id: ALL_ROUTE_ID, route_name: "All Routes" },
          activeSessionId
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [document_type, refreshPreview, selectedRouteId, selectedSampleId, sessionId]
  );

  useEffect(() => {
    const session = resolveSession();
    setSessionId(session.id);
    setSessionName(session.name);
    setSessionStatus(session.status);

    const handleMessage = (event) => {
      if (event.data && event.data.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      loadPageData({ sid: sessionId });
      return;
    }
    loadPageData({ soft: true, sid: sessionId });
  }, [sessionId, document_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRouteSelect = useCallback(
    (routeId) => {
      const nextId = String(routeId || ALL_ROUTE_ID);
      if (String(selectedRouteId) === nextId) {
        return;
      }
      setSelectedRouteId(nextId);
      const route =
        nextId === ALL_ROUTE_ID
          ? { route_id: ALL_ROUTE_ID, route_name: "All Routes" }
          : routes.find((r) => String(r.route_id) === nextId) || {
              route_id: nextId,
              route_name: "Selected route",
            };
      refreshPreview(selectedSample, route, sessionId);
    },
    [routes, selectedSample, selectedRouteId, sessionId, refreshPreview]
  );

  const handleSampleSelect = useCallback(
    (sample) => {
      if (!sample) return;
      const nextId = String(sample.sample_id || "");
      if (String(selectedSampleId) === nextId && nextId !== "") {
        return;
      }
      setSelectedSampleId(nextId);
      refreshPreview(sample, selectedRoute, sessionId);
    },
    [selectedRoute, selectedSampleId, sessionId, refreshPreview]
  );

  const HandleSelectedSession = (sid, sname, sstatus) => {
    bootstrappedRef.current = true;
    setSessionId(sid);
    setSessionName(sname);
    setSessionStatus(sstatus);
    setIsOpenSessionDialog(false);
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page tr-page trep-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Transport Report</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaFileAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Transport Report</h1>
              <p className="sm-hero__sub">
                {formatCount(routes.length)} route
                {routes.length === 1 ? "" : "s"} · Session {sessionName || "—"}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <button
              type="button"
              onClick={() => loadPageData({ soft: true, sid: sessionId })}
              disabled={refreshing || loading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="tr-content trep-content">
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              setIsOpenSessionDialog(true);
            }}
            className="trep-session"
          >
            <span>
              Session: {sessionName || "—"}
              {sessionStatus ? ` (${sessionStatus})` : ""}
            </span>
            <img src={uploadIcon} alt="" className="trep-session__icon" />
          </Link>

          {loading ? (
            <div className="tr-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              {samples.length > 1 ? (
                <section className="trep-panel" aria-label="Printable samples">
                  <div className="trep-panel__head">
                    <h2 className="trep-panel__title">
                      <FaFileAlt aria-hidden /> Report layout
                    </h2>
                  </div>
                  <div className="trep-chips" role="group" aria-label="Samples">
                    {samples.map((sample, index) => {
                      const id = String(sample.sample_id || index);
                      const active =
                        String(selectedSample?.sample_id || "") ===
                        String(sample.sample_id || "");
                      return (
                        <button
                          key={id}
                          type="button"
                          className={`trep-chip${active ? " is-active" : ""}`}
                          onClick={() => handleSampleSelect(sample)}
                        >
                          {active ? (
                            <FaCheck aria-hidden />
                          ) : (
                            <FaFileAlt aria-hidden />
                          )}
                          <span>
                            {sample.sample_name ||
                              sample.file_name ||
                              `Sample ${index + 1}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section className="trep-panel" aria-label="Routes">
                <div className="trep-panel__head">
                  <div className="min-w-0">
                    <h2 className="trep-panel__title">
                      <FaRoute aria-hidden /> Select route
                    </h2>
                    <p className="trep-panel__sub">
                      Showing: {selectedRoute.route_name}
                    </p>
                  </div>
                </div>
                <div className="trep-chips" role="group" aria-label="Routes">
                  <button
                    type="button"
                    className={`trep-chip${
                      selectedRouteId === ALL_ROUTE_ID ? " is-active" : ""
                    }`}
                    onClick={() => handleRouteSelect(ALL_ROUTE_ID)}
                  >
                    {selectedRouteId === ALL_ROUTE_ID ? (
                      <FaCheck aria-hidden />
                    ) : (
                      <FaBus aria-hidden />
                    )}
                    <span>All Routes</span>
                  </button>
                  {routes.map((route) => {
                    const active =
                      String(selectedRouteId) === String(route.route_id);
                    return (
                      <button
                        key={route.route_id}
                        type="button"
                        className={`trep-chip${active ? " is-active" : ""}`}
                        onClick={() => handleRouteSelect(route.route_id)}
                      >
                        {active ? (
                          <FaCheck aria-hidden />
                        ) : (
                          <FaRoute aria-hidden />
                        )}
                        <span>{route.route_name || "Unnamed route"}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section
                className="trep-preview"
                aria-label="Student list preview"
              >
                <div className="trep-preview__head">
                  <h2 className="trep-preview__title">Student list</h2>
                  <p className="trep-preview__sub">
                    Loaded in preview · {selectedRoute.route_name}
                  </p>
                </div>

                <div className="trep-preview__frame-wrap">
                  {iframeLoading ? (
                    <div className="trep-preview__loader">
                      <MyLoader />
                    </div>
                  ) : null}
                  {fileUrl ? (
                    <Iframe
                      key={fileUrl}
                      ref={iframeRef}
                      src={fileUrl}
                      width="100%"
                      height={height}
                      className="trep-preview__iframe"
                      id="transportReportFrame"
                      scrolling="no"
                      onLoad={() => setIframeLoading(false)}
                    />
                  ) : (
                    <div className="trep-preview__empty">
                      Select a route to load the student list.
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={HandleSelectedSession}
        />
      ) : null}
    </div>
  );
};

export default TransportReport;
